import tokenService from 'services/tokenService';
import storageService from 'services/storageService';
import storageKeys, { pkceKeys } from 'services/storageKeys';
import config from 'config';
import { abortSignalTimeout, getCommonHeaders, getFullUrl } from 'redux/helpers/utils';
import { handleOauthCallback } from 'services/authService';
import { setAuthProvider } from 'helpers';
import i18next from 'i18next';

const fetchNewTokenUrl = getFullUrl('/oauth/token');

// Bound the refresh so a stalled fetch can't pin the refresh lock forever.
// An abort is standardised to "Failed to fetch" and treated as a transient failure.
// Keep below ensureValidToken's lock-acquisition timeout (30s) so the winning tab
// releases the lock before other tabs time out waiting for it and log out.
const fetchTimeoutMs = 25 * 1000;

const fetchNewToken = async () => {
  const [accessToken, refreshToken, fcmToken, apnsToken] = await Promise.all([
    tokenService.getAccessToken(),
    tokenService.getRefreshToken(),
    tokenService.getFcmToken(),
    tokenService.getApnsToken(),
  ]);

  return fetch(fetchNewTokenUrl, {
    method: 'POST',
    priority: 'high',
    mode: 'cors',
    signal: abortSignalTimeout(fetchTimeoutMs),
    headers: {
      'Content-Type': 'application/json',
      ...getCommonHeaders(accessToken),
    },
    body: JSON.stringify({
      ...(fcmToken && { fcm_token: fcmToken }),
      ...(apnsToken && { apns_token: apnsToken }),

      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: config.passport.clientId,
      scope: '',
    }),
  })
    .catch(() => {
      // Standardise all failed to reach server errors
      throw new TypeError('Failed to fetch');
    })
    .then(response => response.json())
    .then((jsonBody) => {

      if (jsonBody.error) {
        throw new Error(jsonBody.error);
      }

      return Promise.all([
        tokenService.setAccessToken(jsonBody.access_token),
        tokenService.setRefreshToken(jsonBody.refresh_token),
      ]);
    })
    .then(() => true);
};

function base64Url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function pkcePair() {
  const rand = crypto.getRandomValues(new Uint8Array(32));
  const codeVerifier = base64Url(rand);
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = base64Url(digest);

  return { codeVerifier, codeChallenge };
}

const generateAuthUrl = async (prompt) => {
  const { codeVerifier, codeChallenge } = await pkcePair();
  const state = crypto.randomUUID();

  const authUrl = `${config.apiUrl}/oauth/authorize?${
    new URLSearchParams({
      response_type: 'code',
      client_id: config.passport.clientId,
      redirect_uri: `${config.appUrl}/oauth/callback`,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      ...(prompt ? { prompt } : {}), // "none" | "consent" | "login"

      locale: i18next.language,
    })}`;

  return { authUrl, codeVerifier, codeChallenge, state };
};

const exchangeCodeForTokens = async (code) => {

  const codeVerifier = await storageService.getValue(storageKeys.authCodeVerifier);

  return fetch(`${config.apiUrl}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.passport.clientId,
      redirect_uri: `${config.appUrl}/oauth/callback`,
      code_verifier: codeVerifier,
      code,
    }),
  })
    .then(response => response.json())
    .then(response => {
      return Promise.all([
        tokenService.setAccessToken(response.access_token),
        tokenService.setRefreshToken(response.refresh_token),
      ]);
    })
    .finally(() => storageService.removeValues(pkceKeys));
};

const isStartPkceUrl = (location) => {
  const params = new URLSearchParams(location?.search || {});
  const startPkce = params.get('startPkce');

  return startPkce === 'true' || startPkce === '1';
};

const isPassportUrl = async (location) => {
  if (!location) {
    return false;
  }


  if (location?.pathname === '/oauth/callback') {
    return true;
  }

  if (isStartPkceUrl(location)) {
    return true;
  }

  return false;
};

const handlePassportUrl = async (location) => {
  if (!location?.search) {
    throw new Error('Invalid url');
  }

  const params = new URLSearchParams(location.search);

  const error = params.get('error');
  const errorDescription = params.get('error_description');

  if (error) {
    throw new Error(errorDescription);
  }

  const code = params.get('code');
  if (code) {
    await setAuthProvider('passport');
    return handleOauthCallback(code);
  }

  if (isStartPkceUrl(location)) {
    await setAuthProvider('passport');
    return startPkceLogin();
  }

  // Unknown
  throw new Error('Unhandled oauth url!');
};

const startPkceLogin = async () => {

  const { authUrl, state, codeChallenge, codeVerifier } = await generateAuthUrl();

  storageService.setValue(storageKeys.authCodeChallenge, codeChallenge);
  storageService.setValue(storageKeys.authCodeVerifier, codeVerifier);
  storageService.setValue(storageKeys.authState, state);

  if (window.cordova) {
    // Use InAppBrowser or AppBrowser plugin
    const browserRef = window.cordova.InAppBrowser.open(authUrl, '_blank');

    browserRef.addEventListener('loadstart', (event) => {
      const url = new URL(event.url);

      if (url.pathname === '/oauth/callback' && url.searchParams.get('state') === state) {
        const code = url.searchParams.get('code');

        handleOauthCallback(code)
          .then(() => browserRef.close());
      }
    });

  } else {
    window.location.href = authUrl;
  }
};

const isRefreshable = async () => {
  const refreshToken = await tokenService.getRefreshToken();

  // No way to tell for sure unless we try it. Having a token is good enough for now
  return !!refreshToken;
};

export default {
  fetchNewToken,
  isRefreshable,

  startPkceLogin,
  generateAuthUrl,

  isPassportUrl,
  handlePassportUrl,

  exchangeCodeForTokens,
};
